import { DownloadIcon, ExternalLinkIcon, FileTextIcon } from "lucide-react";
import { cn } from "@flint/ui";

export interface GeneratedFileAttachmentProps {
  title: string;
  url?: string | undefined;
  format?: string | undefined;
  mediaType?: string | undefined;
  sizeBytes?: number | undefined;
  className?: string | undefined;
}

export function GeneratedFileAttachment({
  title,
  url,
  format,
  mediaType,
  sizeBytes,
  className,
}: GeneratedFileAttachmentProps) {
  const label = format ?? mediaType ?? "file";
  const sizeLabel = typeof sizeBytes === "number" ? ` · ${formatBytes(sizeBytes)}` : "";

  return (
    <div
      className={cn(
        "w-fit max-w-full self-start rounded-lg border border-line bg-paper px-3 py-3 text-xs",
        className,
      )}
    >
      <div className="flex max-w-full flex-wrap items-center gap-3">
        <div className="grid size-8 shrink-0 place-items-center rounded-md bg-emerald-50 text-emerald-700">
          <FileTextIcon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 max-w-80 flex-1">
          <p className="truncate text-sm font-medium text-ink">{title}</p>
          <p className="mt-0.5 truncate text-xs text-ink-2">
            {label.toUpperCase()}
            {sizeLabel}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {url ? (
            <>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-bg px-2.5 text-xs font-medium text-ink transition-colors hover:bg-bg-2"
              >
                <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden />
                Open
              </a>
              <a
                href={url}
                download
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-ink px-2.5 text-xs font-medium text-paper transition-colors hover:bg-ink/90"
              >
                <DownloadIcon className="h-3.5 w-3.5" aria-hidden />
                Download
              </a>
            </>
          ) : (
            <span className="rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-2">
              Stored
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatBytes(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
